/**
 * ConsensusContainer Component
 *
 * Main container for Consensus UI showing Claim-Level Analysis.
 * Handles data loading, error states, and correlationId propagation.
 *
 * Part of Prompt 3: Consensus shell in Responses tab (no visuals)
 */

import React, { useEffect, useState } from 'react';
import { logger } from '../../lib/logging';
import { selectConsensusData } from './selectors';
import { HeatmapWithComparison } from './components/HeatmapWithComparison';
import type { Comparison, ModelResponse, ScoringResult } from '../../services/comparison';
import type { ConsensusData } from './types';

export interface ConsensusContainerProps {
  comparison: Comparison;
  responses: ModelResponse[];
  scoringResults?: ScoringResult[];
  correlationId?: string;
  // Extended props for demo/testing
  showScores?: boolean;
  confidenceData?: Record<string, Record<string, {
    averageScore: number;
    level?: 'high' | 'medium' | 'low';
    judgeAgreement?: string;
    variance?: number;
  }>>;
  judgeScores?: Record<string, Record<string, Array<{
    judge: string;
    score: number;
    reasoning: string;
    timestamp?: string;
  }>>>;
}

export const ConsensusContainer: React.FC<ConsensusContainerProps> = ({
  comparison,
  responses,
  scoringResults,
  correlationId,
  showScores = true,
  confidenceData,
  judgeScores
}) => {
  const [consensusData, setConsensusData] = useState<ConsensusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mount/unmount logging with correlationId propagation
  useEffect(() => {
    const mountCorrelationId = correlationId || crypto.randomUUID();

    logger.info('consensus.container.mount', undefined, {
      op: 'consensus.container.mount',
      component: 'ConsensusContainer',
      correlationId: mountCorrelationId,
      comparisonId: comparison.id
    });

    return () => {
      logger.info('consensus.container.unmount', undefined, {
        op: 'consensus.container.unmount',
        component: 'ConsensusContainer',
        correlationId: mountCorrelationId,
        comparisonId: comparison.id
      });
    };
  }, [comparison.id, correlationId]);

  // Load consensus data using selector
  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      const data = selectConsensusData(comparison, responses, scoringResults);

      if (!data) {
        setError('Unable to generate consensus data');
      } else {
        setConsensusData(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [comparison, responses, scoringResults]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="consensus-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading consensus analysis...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !consensusData) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="consensus-error">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Unable to Load Consensus</h3>
          <p className="text-slate-400 text-sm">{error || 'Failed to generate consensus data'}</p>
        </div>
      </div>
    );
  }

  // Empty state (no responses)
  if (consensusData.responses.length === 0) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="consensus-empty">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No Responses Available</h3>
          <p className="text-slate-400 text-sm">Add model responses to see consensus analysis</p>
        </div>
      </div>
    );
  }

  // Render Claim-Level Consensus Analysis directly
  return (
    <div data-testid="consensus-container">
      <HeatmapWithComparison
        data={consensusData}
        responses={responses}
        correlationId={correlationId}
        showScores={showScores}
        confidenceData={confidenceData}
      />
    </div>
  );
};
